import { FirestoreError } from "firebase/firestore";

import { CreatedByData } from "../General/CreatedByData";
import { CompanyData } from "./CompanyData";
import { UnlocAuthTokenData, UnlocHelper, UnlocVendor } from "../Property/Integrations/Unloc";
import { FirebaseLockData } from "./FirebaseLockData";
import { FirebaseData } from "../General/FirebaseData";
import { PropertyData } from "../Property/PropertyData";
import CollectionLoader from "../General/CollectionLoader";
import VendorConnectionData from "./VendorConnectionData";
import { LockHolderAccessGroupData } from "./LockHolderAccessGroupData";
import { LockHolderAccessTokenData } from "./LockHolderAccessTokenData";
import { Debug } from "../../../utils/debug";
import { DataLoadedListener, DataLoader } from "../General/DataLoadedListener";
import { FirebaseKeyData } from "./FirebaseKeyData";
import { FirebaseLockholderRoleData } from "./FirebaseLockholderRoleData";
import { UnlocData, UnlocDataHelper } from "./ExternalData";
import { UnlocKey, UnlocKeyEvent, UnlocLock, UnlocRole, UnlocVendorConnection } from "../Property/Integrations/Unloc/UnlocTypes";
import { UnlocAPI } from "../Property/Integrations/Unloc/UnlocAPI";

export class LockHolderData extends FirebaseData {
  // Firebase Data
  company: CompanyData;
  unlocId: string = "";
  name: string = "";
  countryIso: string = "";
  organizationId: string = "";
  organizationIdSuffix: string = "";
  createdBy?: CreatedByData;

  fbVendorConnections: CollectionLoader<VendorConnectionData>;
  fbLocks: CollectionLoader<FirebaseLockData>;
  fbKeys: CollectionLoader<FirebaseKeyData>;
  fbRoles: CollectionLoader<FirebaseLockholderRoleData>;
  accessGroups: CollectionLoader<LockHolderAccessGroupData>;
  accessTokens: CollectionLoader<LockHolderAccessTokenData>;

  // Unloc Data
  hasError: boolean = false;
  scopedAuth: DataLoader<UnlocAuthTokenData>;

  keys: UnlocData<UnlocKey> = { datas: [], loading: false, loaded: false, listeners: [] };
  roles: UnlocData<UnlocRole> = { datas: [], loading: false, loaded: false, listeners: [] };
  vendorConnections: UnlocData<UnlocVendorConnection> = { datas: [], loading: false, loaded: false, listeners: [] };
  locks: UnlocData<UnlocLock> = { datas: [], loading: false, loaded: false, listeners: [] };
  keyEvents: UnlocData<UnlocKeyEvent> = { datas: [], loading: false, loaded: false, listeners: [] };

  constructor(id: string, collection: string, parent?: FirebaseData) {
    super(id, collection, parent);
    this.company = parent as CompanyData;
    this.fbVendorConnections = new CollectionLoader("vendorConnections", this, VendorConnectionData);
    this.fbLocks = new CollectionLoader("locks", this, FirebaseLockData);
    this.fbKeys = new CollectionLoader("keys", this, FirebaseKeyData);
    this.fbRoles = new CollectionLoader("roles", this, FirebaseLockholderRoleData);
    this.accessGroups = new CollectionLoader("accessGroups", this, LockHolderAccessGroupData);
    this.accessTokens = new CollectionLoader("accessTokens", this, LockHolderAccessTokenData);
    this.scopedAuth = new DataLoader();
  }

  setup(data: any) {
    this.unlocId = typeof data.unlocId === "string" ? data.unlocId : "";
    this.name = typeof data.name === "string" ? data.name : "";
    this.countryIso = typeof data.countryIso === "string" ? data.countryIso : "";
    this.organizationId = typeof data.organizationId === "string" ? data.organizationId : "";
    this.organizationIdSuffix = typeof data.organizationIdSuffix === "string" ? data.organizationIdSuffix : "";

    if (super.isValidObject(data.createdBy)) {
      this.createdBy = new CreatedByData(data.createdBy);
    }
    super.setup(data);
  }

  save(completion: (error: FirestoreError | undefined) => void) {
    this.saveUpdateWithCompletionHandler(
      {
        name: this.name,
        countryIso: this.countryIso,
        organizationId: this.organizationId,
        organizationIdSuffix: this.organizationIdSuffix,
      },
      (e) => completion(e)
    );
  }

  createLock(unlocData: UnlocLock, property: PropertyData | undefined, completion: (error?: FirestoreError) => void) {
    this.fbLocks
      .createData({
        name: unlocData.name,
        propertyId: property?.id ?? "",
        unlocLockId: unlocData.id,
      })
      .then(() => {
        console.log("Created firebase lock");
        completion(undefined);
      })
      .catch((error: FirestoreError) => {
        console.error("Error creating firebase lock");
        console.error(error);
        completion(error);
      });
  }

  getFirebaseLockForUnlocLock(unlocLockId: string): FirebaseLockData | undefined {
    return this.fbLocks.documents.find((fb) => fb.unlocLockId === unlocLockId);
  }

  getKeys(completion: (keys: UnlocKey[]) => void) {
    if (this.keys.loaded) {
      completion(this.keys.datas);
    } else {
      this.keys.listeners.push(completion);
      if (!this.keys.loading) {
        this.keys.loading = true;
        this.getScopedAuthData((auth) => {
          if (auth) {
            const API_LIMIT = 2500;
            let startAt: string | undefined = undefined;
            this.keys.datas = [];

            const load = () => {
              UnlocAPI.getKeys(this, { limit: "" + API_LIMIT, startAfterId: startAt, excludeStates: "error" })
                .then((response) => {
                  startAt = undefined;
                  this.keys.datas.push(...response);
                  if (response.length === API_LIMIT) {
                    startAt = response[API_LIMIT - 1].id;
                  }
                })
                .catch((error) => {
                  console.error(error);
                })
                .finally(() => {
                  if (startAt) {
                    load();
                  } else {
                    UnlocDataHelper.callLoadingDataListeners(this.keys);
                  }
                });
            };

            load();
          } else {
            UnlocDataHelper.callLoadingDataListeners(this.keys);
          }
        });
      }
    }
  }

  getRoles(completion: (roles: UnlocRole[]) => void) {
    if (this.roles.loaded) {
      completion(this.roles.datas);
    } else {
      this.roles.listeners.push(completion);
      if (!this.roles.loading) {
        this.roles.loading = true;
        this.getScopedAuthData((auth) => {
          if (auth) {
            UnlocAPI.getRoles(this)
              .then((response) => {
                this.roles.datas = response;
              })
              .finally(() => {
                UnlocDataHelper.callLoadingDataListeners(this.roles);
              });
          } else {
            UnlocDataHelper.callLoadingDataListeners(this.roles);
          }
        });
      }
    }
  }

  getVendorConnections(completion: (vendorConnections: UnlocVendorConnection[]) => void, forceRefresh?: boolean) {
    if (this.vendorConnections.loaded && !forceRefresh) {
      completion(this.vendorConnections.datas);
    } else {
      this.vendorConnections.listeners.push(completion);
      if (!this.vendorConnections.loading) {
        this.vendorConnections.loading = true;
        this.getScopedAuthData((auth) => {
          if (auth) {
            UnlocAPI.getVendorConnections(this)
              .then((response) => {
                this.vendorConnections.datas = response;
              })
              .finally(() => {
                UnlocDataHelper.callLoadingDataListeners(this.vendorConnections);
              });
          } else {
            UnlocDataHelper.callLoadingDataListeners(this.vendorConnections);
          }
        });
      }
    }
  }

  getLocks(completion: (locks: UnlocLock[]) => void, forceUpdate: boolean) {
    if (this.locks.loaded) {
      completion(this.locks.datas);
    } else {
      this.locks.listeners.push(completion);
      if (!this.locks.loading) {
        this.locks.loading = true;
        this.getScopedAuthData((auth) => {
          if (auth) {
            UnlocAPI.getLocks(this)
              .then((response) => {
                this.locks.datas = response;
              })
              .finally(() => {
                UnlocDataHelper.callLoadingDataListeners(this.locks);
              });
          } else {
            UnlocDataHelper.callLoadingDataListeners(this.locks);
          }
        });
      }
    }
  }

  getKeyEvents(completion: (events: UnlocKeyEvent[]) => void) {
    if (this.keyEvents.loaded) {
      completion(this.keyEvents.datas);
    } else {
      this.keyEvents.listeners.push(completion);
      if (!this.keyEvents.loading) {
        this.keyEvents.loading = true;
        this.getScopedAuthData((auth) => {
          if (auth) {
            if (this.locks.loaded) {
              Promise.all(
                this.locks.datas.map((lock) =>
                  UnlocAPI.getKeyEvents(this, lock).then((response) => {
                    this.keyEvents.datas.push(...response);
                  })
                )
              ).finally(() => {
                UnlocDataHelper.callLoadingDataListeners(this.keyEvents);
              });
            } else {
              this.getLocks(() => {
                Promise.all(
                  this.locks.datas.map((lock) =>
                    UnlocAPI.getKeyEvents(this, lock).then((response) => {
                      this.keyEvents.datas.push(...response);
                    })
                  )
                ).finally(() => {
                  UnlocDataHelper.callLoadingDataListeners(this.keyEvents);
                });
              }, false);
            }
          } else {
            UnlocDataHelper.callLoadingDataListeners(this.keyEvents);
          }
        });
      }
    }
  }

  getScopedAuthData(completion: (auth?: UnlocAuthTokenData) => void) {
    Debug.print("getScopedAuthData " + this.id);
    if (this.scopedAuth.data) {
      Debug.print("Has scoped auth " + this.id);
      completion(this.scopedAuth.data);
      return;
    }

    for (const token of this.accessTokens.documents) {
      if (token.token && token.expireDate && token.expireDate > new Date()) {
        Debug.print("Has firebase scoped auth " + this.id);
        this.scopedAuth.data = token.token;
        completion(token.token);
        return;
      } else {
        if (!token.expireDate || token.expireDate < new Date()) {
          token.deleteDocumentWithCompletionHandler(() => {});
        }
      }
    }

    Debug.print("Generating scoped auth " + this.id);
    this.scopedAuth.loadedListeners.push(new DataLoadedListener(completion, this.scopedAuth));
    if (!this.scopedAuth.loading) {
      this.scopedAuth.loading = true;
      UnlocHelper.generateScopedUnlocAuthToken(this, (data) => {
        this.scopedAuth.loading = false;
        this.scopedAuth.data = data;
        this.hasError = data === undefined;
        this.scopedAuth.callListeners();
        this.scopedAuth.loadedListeners = [];
      });
    }
  }

  async createVendorConnection(vendor: UnlocVendor, fields: { [key: string]: any }): Promise<UnlocVendorConnection> {
    if (!this.scopedAuth?.data?.access_token) return Promise.reject();
    if (vendor.id === "virtual") fields["locksToCreate"] = 10;

    const result = await UnlocAPI.createVendorConnection(this, { vendor: vendor.id, ...fields });
    this.vendorConnections.datas.push(result);

    const saveData: { [key: string]: any } = {
      unlocId: result.id,
      vendor: vendor.id,
    };
    for (const field of vendor.fields) {
      if (!field.disableSave && fields[field.key]) {
        saveData[field.key] = fields[field.key];
      }
    }

    this.fbVendorConnections.createData(saveData);
    return result;
  }
}

